/**
 * Sticky Header
 * Adds smooth scroll animation to the header
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    scrollThreshold: 100,
    headerSelector: '.header',
    topstripSelector: '[data-topstrip]',
    stickyClass: 'is-sticky',
    hiddenClass: 'is-hidden',
    debounceDelay: 5
  };

  let header = null;
  let topstrip = null;
  let section = null;
  let isBlogLayout = false;
  let lastScrollPosition = 0;
  let ticking = false;
  let resizeObserver = null;

  /**
   * Publish the header's live rendered height as --header-height on :root.
   * The fixed header is out of flow, so the rest of the layout (hero clearance,
   * scroll-padding for anchors) depends on this value to know how much space the
   * header actually occupies. Height changes on sticky toggle, logo scale and
   * resize, so we re-measure via ResizeObserver rather than hardcoding.
   */
  function publishHeaderHeight() {
    if (!header) return;
    // The header is position:fixed and offset from the top of the viewport
    // (e.g. top: ~45px on desktop, plus the contact topstrip above it), so the
    // space it actually occupies is its height PLUS that top offset. Layout
    // clearance (hero padding, anchor scroll-padding) needs the bottom edge,
    // not just the bar height — otherwise content tucks under the bar at some
    // viewports. On mobile top resolves to 0px, matching the old behaviour.
    const topOffset = parseFloat(getComputedStyle(header).top) || 0;
    const height = Math.round(header.getBoundingClientRect().height + topOffset);
    document.documentElement.style.setProperty('--header-height', `${height}px`);
  }

  /**
   * Initialize the sticky header functionality
   */
  function init() {
    header = document.querySelector(CONFIG.headerSelector);
    topstrip = document.querySelector(CONFIG.topstripSelector);

    if (!header) {
      console.warn('Sticky Header: Header element not found');
      return;
    }

    // On blog/article templates the header sits in normal document flow while
    // at the top of the page (so it scrolls away instead of pinning). When it
    // becomes sticky it switches to position:fixed and leaves the flow — which
    // would collapse its section and yank content upward. We reserve that exact
    // space on the section the instant we pin, so the swap causes zero shift.
    section = header.closest('.shopify-section') || header.parentElement;
    isBlogLayout =
      document.body.classList.contains('template-blog') ||
      document.body.classList.contains('template-article');

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', publishHeaderHeight, { passive: true });

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(publishHeaderHeight);
      // Observe border-box so padding changes on the sticky toggle re-fire it.
      resizeObserver.observe(header, { box: 'border-box' });
    }

    publishHeaderHeight();
    checkHeaderState();
  }

  /**
   * Handle scroll events with requestAnimationFrame for smooth performance
   */
  function handleScroll() {
    lastScrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    if (!ticking) {
      window.requestAnimationFrame(function() {
        checkHeaderState();
        ticking = false;
      });
      
      ticking = true;
    }
  }

  /**
   * Check if header should be sticky based on scroll position
   */
  function checkHeaderState() {
    const shouldStick = lastScrollPosition > CONFIG.scrollThreshold;
    const isStuck = header.classList.contains(CONFIG.stickyClass);
    if (shouldStick === isStuck) return;

    if (shouldStick) {
      // Measure the in-flow height BEFORE pinning, then freeze it on the
      // section so the now-fixed header leaves no gap to collapse.
      if (isBlogLayout && section) {
        section.style.minHeight = `${section.getBoundingClientRect().height}px`;
      }
      header.classList.add(CONFIG.stickyClass);
      topstrip?.classList.add(CONFIG.hiddenClass);
    } else {
      header.classList.remove(CONFIG.stickyClass);
      topstrip?.classList.remove(CONFIG.hiddenClass);
      // Back at the top: header rejoins the flow and re-fills the section on its
      // own, so release the reservation (heights match → still no shift).
      if (isBlogLayout && section) {
        section.style.minHeight = '';
      }
    }
  }

  /**
   * Clean up event listeners (useful for SPA-style navigation)
   */
  function destroy() {
    if (header) {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', publishHeaderHeight);
      resizeObserver?.disconnect();
      resizeObserver = null;
      header.classList.remove(CONFIG.stickyClass);
      topstrip?.classList.remove(CONFIG.hiddenClass);
      if (section) section.style.minHeight = '';
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose destroy method for cleanup if needed
  window.SiteStickyHeader = {
    destroy: destroy,
    init: init
  };

})();

